import { GuardianCoGuardianInviteScreen } from '@/features/guardian/screens/guardian-co-guardian-invite-screen';
import { GuardianInvitationErrorScreen } from '@/features/guardian/screens/guardian-invitation-error-screen';
import { SplashScreen } from '@/features/splash/screens/splash-screen';
import { withAuth, withRoleAccess, withUnAuth } from '@/hocs/with-protect';
import { navigationIntegration } from '@/libs/sentry';
import { AuthStack } from '@/navigations/auth-stack';
import { DevelopmentStack } from '@/navigations/development-stack';
import { GuardianStack } from '@/navigations/guardian-stack';
import { navigationRef } from '@/navigations/navigation-ref';
import type { RootStackParamList } from '@/navigations/types';
import { UnAuthStack } from '@/navigations/unauth-stack';
import { NavigationContainer, type LinkingOptions } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import React from 'react';
import { Platform } from 'react-native';
import BootSplash from 'react-native-bootsplash';

const Stack = createNativeStackNavigator<RootStackParamList>();

/** Walk a navigation state down to the currently focused (deepest active) route. */
type MinimalNavState = {
  index?: number;
  routes: Array<{ name: string; params?: Record<string, unknown>; state?: MinimalNavState }>;
};
const getFocusedRoute = (state: MinimalNavState): MinimalNavState['routes'][number] => {
  let route = state.routes[state.index ?? state.routes.length - 1];
  while (route?.state) {
    const child = route.state;
    route = child.routes[child.index ?? child.routes.length - 1];
  }
  return route;
};

/**
 * Public URL mapping shared by web and native. Guardian invitations live at the root so they
 * remain accessible without authentication; OIDC continues to resolve inside the UnAuth tree.
 *
 * The default `getStateFromPath` still parses the incoming `/sso-login/:uid` deep-link on
 * entry. But merely providing `linking` makes React Navigation reflect the WHOLE navigation
 * state into the address bar (auto-deriving paths from screen names + params as query strings)
 * — which the app didn't do before linking existed. `getPathFromState` below reins that back
 * in: it keeps `/sso-login/:uid` visible while the SsoLogin screen is focused, and collapses
 * every other screen to a clean `/` (the pre-linking behavior).
 */
const linkingPrefixes = [
  'uskidgolf://',
  // Accept every env's App Link / Universal Link host. A native build only ever
  // *receives* links for the host it declares (Android manifest ${appLinkHost} /
  // iOS associated-domains), so listing all is harmless and keeps parsing env-agnostic.
  'https://player-pathway.hnl.works',
  'https://stg-player-pathway.hnl.works',
  ...(Platform.OS === 'web' ? [window.location.origin] : []),
];

const linking: LinkingOptions<RootStackParamList> = {
  prefixes: linkingPrefixes,
  config: {
    screens: {
      GuardianInvitation: 'invite/:token',
      UnAuth: {
        screens: {
          SsoLogin: 'sso-login/:uid',
          ShopBridge: 'shop-bridge',
          SilentLogin: 'silent-login',
        },
      },
    },
  },
  getPathFromState: state => {
    const focused = getFocusedRoute(state as unknown as MinimalNavState);
    if (focused?.name === 'GuardianInvitation') {
      const token = focused.params?.token;
      return typeof token === 'string' && token
        ? `/invite/${encodeURIComponent(token)}`
        : '/invite';
    }
    if (focused?.name === 'SsoLogin') {
      const uid = focused.params?.uid;
      return typeof uid === 'string' && uid
        ? `/sso-login/${encodeURIComponent(uid)}`
        : '/sso-login';
    }
    if (focused?.name === 'ShopBridge') {
      const code = focused.params?.code;
      return typeof code === 'string' && code
        ? `/shop-bridge?code=${encodeURIComponent(code)}`
        : '/shop-bridge';
    }
    if (focused?.name === 'SilentLogin') {
      return '/silent-login';
    }
    return '/';
  },
};

/**
 * Wrapped screen components are created ONCE, at module scope — NOT inline in the render below.
 *
 * Calling an HOC inline (`component={withRoleAccess(GuardianStack, 'guardian')}`) returns a brand
 * new component identity on every `RootNavigator` re-render. React Navigation treats a changed
 * `component` as a different screen and REMOUNTS the route, which drops the nested navigator's
 * state and snaps it back to its default first screen — for the Guardian stack that's `Onboarding`
 * (no params → wizard step 1). That's what bounced an already-onboarded guardian off the dashboard
 * into onboarding whenever an unrelated re-render landed (common under weak network, where a late
 * token/store update triggers one). Stable references keep the mounted nested state intact.
 */
const AuthStackScreen = withAuth(AuthStack);
const GuardianStackScreen = withRoleAccess(GuardianStack, 'guardian');
const UnAuthStackScreen = withUnAuth(UnAuthStack);

/**
 * Root stack: Splash, Auth, UnAuth (and Development in __DEV__).
 * initialRouteName = Splash. Splash replaces to Auth/UnAuth when done; login replaces to Auth.
 */
export function RootNavigator({ onReady }: { onReady: () => void }) {
  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onReady={() => {
        BootSplash.hide();
        navigationIntegration.registerNavigationContainer(navigationRef);
        onReady();
      }}>
      <Stack.Navigator screenOptions={{ headerShown: false }} initialRouteName="Splash">
        <Stack.Screen name="Splash" component={SplashScreen} />
        <Stack.Screen name="GuardianInvitation" component={GuardianCoGuardianInviteScreen} />
        <Stack.Screen name="GuardianInvitationError" component={GuardianInvitationErrorScreen} />
        <Stack.Screen name="Auth" component={AuthStackScreen} />
        <Stack.Screen name="Guardian" component={GuardianStackScreen} />
        {/* <Stack.Screen name="RoleSelect" component={withAuth(RoleSelectScreen)} /> */}
        <Stack.Screen name="UnAuth" component={UnAuthStackScreen} />
        {__DEV__ && <Stack.Screen name="Development" component={DevelopmentStack} />}
      </Stack.Navigator>
    </NavigationContainer>
  );
}
